import React, { useState, useEffect } from 'react';
import axios from 'axios';

const TeamMembers = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    role: 'TeamMember',
    mobile: '', // Added mobile field
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formError, setFormError] = useState('');

  const token = localStorage.getItem('authToken');

  // Fetch existing team members
  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get('https://astrovidhya.in/api/team-members', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeamMembers(response.data);
    } catch (err) {
      console.error('Error fetching team members:', err);
      setError('Failed to fetch team members. Please try again.');
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation: Ensure all fields are filled
    if (!formData.name || !formData.username || !formData.password || !formData.mobile) {
      setFormError('All fields are required, including mobile.');
      return;
    }

    try {
      await axios.post('https://astrovidhya.in/api/team-members', formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Success Message
      setSuccessMessage('Team member added successfully!');
      setFormData({ name: '', username: '', password: '', role: 'TeamMember', mobile: '' }); // Reset form
      fetchTeamMembers(); // Refresh team members list
    } catch (err) {
      console.error('Error adding team member:', err);
      setFormError('Failed to add team member. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this team member?')) {
      try {
        await axios.delete(`https://astrovidhya.in/api/team-members/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccessMessage('Team member deleted successfully!');
        fetchTeamMembers(); // Refresh the list
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        console.error('Error deleting team member:', err);
        setError('Failed to delete team member. Please try again.');
      }
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Manage Team Members</h2>

        {/* Form Section */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 font-medium mb-1">Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-1">Username</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-1">Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-1">Role</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500"
            >
              <option value="TeamMember">Team Member</option>
              <option value="Admin">Admin</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-1">Mobile</label>
            <input
              type="text"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          {formError && <p className="text-red-500">{formError}</p>}
          {successMessage && <p className="text-green-500">{successMessage}</p>}
          <button
            type="submit"
            className="bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 transition duration-200"
          >
            Add Team Member
          </button>
        </form>

        {/* Team Members List */}
        <h3 className="text-xl font-bold mt-8 mb-4 text-gray-800">Existing Team Members</h3>
        {error && <p className="text-red-500">{error}</p>}
        <table className="min-w-full border-collapse bg-white shadow-md rounded-lg overflow-hidden">
          <thead className="bg-gray-200">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Name</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Username</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Role</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Mobile</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {teamMembers.map((member) => (
              <tr key={member._id} className="border-t border-gray-300">
                <td className="px-4 py-2">{member.name}</td>
                <td className="px-4 py-2">{member.username}</td>
                <td className="px-4 py-2">{member.role}</td>
                <td className="px-4 py-2">{member.mobile || 'N/A'}</td>
                <td className="px-4 py-2">
                  <button
                    className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600"
                    onClick={() => handleDelete(member._id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {teamMembers.length === 0 && (
          <p className="text-gray-500 text-center mt-4">No team members found.</p>
        )}
      </div>
    </div>
  );
};

export default TeamMembers;
